/**
 * overlayrefresh.ts — how many overlay passes a burst of refresh requests is worth, as a PURE
 * decision.
 *
 * WHY THIS FILE EXISTS
 * --------------------
 * Andrei, 2026-08-27: "сильно замедлилась отрисовка индикаторов на 15m". Card t_10c01825.
 *
 * MEASURED, not assumed (`process/reviews/t_10c01825/FINDINGS.md`, both probes committed beside it):
 * one page load asks each (asset, tf, component) EXACTLY ONCE -- 21 calls, 21 distinct, with all
 * seven overlays on three panes -- so the card's hypothesis that the history splice re-asks is
 * refuted, and `history.ts::HistoryState.armed` is why (a load with no gesture splices nothing).
 * What DOES multiply is a burst of refresh requests for ONE pane:
 *
 *     TWO knob changes 200 ms apart            18 calls,  9 distinct,  9 urls fetched 2x
 *     15m checkbox on then off, 150 ms apart     7 calls,  4 distinct,  3 urls fetched 2x
 *
 * The second row is the argument: the pane ENDS IN THE STATE IT STARTED IN and the second pass
 * re-fetches `swings`, `setup` and `ranges` at the same `first_time` with the same knobs. On 15m a
 * layer costs a median 2533 ms server-side (86 deployed calls), against 95-630 ms for the bars of
 * the same series, so a superseded pass is seconds of pure waste.
 *
 * WHAT THIS IS AND IS NOT
 * -----------------------
 * IT IS A SUPERSESSION RULE, NOT A CACHE. The card forbids caching an overlay response by
 * (asset, tf) without accounting for the forming bar -- the week-box D1/D2 defect of t_420df4d4,
 * where drawn geometry lagged what was computed. Nothing here remembers a payload. Every pass that
 * runs goes to the network and reads the pane's window, knobs and toggles at the moment it runs, so
 * a level that depends on the forming bar stays exactly as live as it was.
 *
 * THE PROPERTY THAT MAKES DROPPING A PASS SAFE, and the one the card demands
 * (`НЕ давить повторный запрос ПОСЛЕ сплайса -- там он нужен`):
 *
 *     EVERY request is followed by a pass that STARTS STRICTLY AFTER that request was made.
 *
 * A request arriving while a pass is in flight cannot be served by that pass -- it began before the
 * request and may have read pre-splice state -- so it schedules a follow-up. Two requests arriving
 * during one pass share ONE follow-up, because a single later pass starts after both of them and
 * reads the state both were asking about. That is where the saving comes from, and it is the whole
 * of it: a request that arrives while NOTHING is running always starts a pass immediately.
 *
 * So after a history splice the refresh still happens -- `loadOlderBars` awaits the promise this
 * scheduler returns, and that promise resolves only when a pass that started after the splice has
 * finished. Coalescing can never turn N requests into ZERO passes; it turns them into one.
 *
 * WHY A MODULE AND NOT AN `if` IN main.ts. Gate 5 (vitest) exists because a state bug living inside
 * `main.ts` was structurally unreachable by pytest and shipped green through the other gates. The
 * chain this replaces is four lines inside `refreshOverlays`, and "did the second request get its
 * own pass" is precisely the question a chart, a DOM and a backend make impossible to ask.
 */

/** One pane's scheduling state. `null` for a pane with nothing running. */
type PaneRun = {
  /** The pass currently executing. */
  running: Promise<void>;
  /**
   * The same pass with its rejection SWALLOWED, which is what a follow-up chains onto.
   *
   * Two reasons it is a separate field. A rejection nobody handles is an unhandled-rejection
   * warning in the console -- the console Andrei reads to report bugs -- and chaining the raw
   * promise would make the follow-up's own error handling depend on whether a caller happened to
   * await the predecessor. `running` is still what a CALLER gets, so `await refreshOverlays(pane)`
   * keeps failing where it failed before.
   */
  settled: Promise<void>;
  /**
   * The follow-up promised to every request that arrived while `running` was in flight, or `null`
   * when no such request has arrived yet.
   *
   * ONE follow-up, however many requests land: they are all asking for state that a single later
   * pass will read. This field IS the coalescing.
   */
  queued: Promise<void> | null;
};

/**
 * Per-pane overlay refresh scheduling.
 *
 * `run` is injected (it is `refreshOverlaysOnce` in `main.ts`, which needs a chart, a DOM and a
 * backend) so every decision here is witnessed without any of them.
 */
export class OverlayRefreshScheduler {
  private readonly state = new Map<string, PaneRun>();

  /** How many passes this scheduler has actually STARTED, per pane. The saving, measurable. */
  private readonly startedCount = new Map<string, number>();

  constructor(private readonly run: (paneId: string) => Promise<void>) {}

  /**
   * Ask for THIS pane's overlays to be refreshed, and resolve when a pass that started after this
   * call has finished.
   *
   * SERIALISED PER PANE, NOT PER PROCESS, unchanged from what this replaces: the ordering exists so
   * a slow pass cannot paint over a newer pass OF THE SAME SERIES, and each pane owns its own
   * series, markers and primitives. Two panes cannot overwrite each other's geometry (measured
   * 2026-07-31: 6776 ms sequential vs 3782 ms parallel for a three-pane load).
   */
  request(paneId: string): Promise<void> {
    const cur = this.state.get(paneId);
    if (cur === undefined) return this.start(paneId);
    // A pass is in flight. It began BEFORE this request, so it cannot answer it -- but one
    // follow-up answers this request and every other that lands before it starts.
    if (cur.queued !== null) return cur.queued;
    const queued = cur.settled.then(() => this.start(paneId));
    cur.queued = queued;
    return queued;
  }

  /** Passes STARTED for this pane since construction. For witnesses and for the probe. */
  started(paneId: string): number {
    return this.startedCount.get(paneId) ?? 0;
  }

  /** Is a pass in flight for this pane? */
  busy(paneId: string): boolean {
    return this.state.has(paneId);
  }

  /**
   * Begin a pass NOW and register it as this pane's in-flight run.
   *
   * The entry is installed BEFORE awaiting anything, so a request made synchronously after this
   * returns sees a busy pane and queues rather than starting a second concurrent pass. It is
   * removed only if it is still the entry this call installed -- a `queued` follow-up replaces it
   * on its own way in, and clearing blindly would strand the successor's bookkeeping.
   *
   * AND IT IS NOT REMOVED AT ALL WHILE A FOLLOW-UP IS OWED (`entry.queued !== null`). That clause
   * is the per-pane serialisation, and without it the invariant this class claims to preserve is
   * BROKEN across one microtask -- measured by review round 1 of card `t_10c01825`
   * (`process/reviews/t_10c01825/review_r1_serialisation.test.ts`: the chain this replaced ran
   * maxConcurrent 1, the first scheduler ran maxConcurrent 2 on the same three requests). The
   * mechanism: this `finally` runs BEFORE `running` resolves, while the follow-up chains onto
   * `settled` (= `running.catch(...)`), a SEPARATE promise settling one microtask later. Delete the
   * entry here and the pane reads IDLE in that gap, so a request made from a continuation of the
   * returned promise -- the shape of every awaited `refreshOverlays(pane)` call site in `main.ts`
   * -- starts a SECOND concurrent pass and the two interleave their writes to `pane.markers` /
   * `pane.range.setData` / `pane.breaks.setData`, painting a blend of two answers. Keeping the
   * entry until the successor's `start()` REPLACES it (`this.state.set` below is unconditional)
   * closes the gap without a lock: the pane is continuously non-idle from the predecessor's first
   * bar to the successor's last.
   *
   * A request landing in that window is still not dropped -- it reads `cur.queued !== null` and is
   * given the follow-up, which by construction starts after it.
   */
  private start(paneId: string): Promise<void> {
    this.startedCount.set(paneId, this.started(paneId) + 1);
    // A ONE-FIELD HOLDER rather than a bare `let`: the cleanup below runs inside the async body and
    // must compare against the entry this call installs, which does not exist until the promise
    // does. tsc cannot see that the assignment precedes the read (the read is in a callback), so
    // the indirection is what makes the identity check type-check -- the same shape as
    // `refreshOverlaysOnce`'s `liquidity` holder in main.ts.
    const self: { entry: PaneRun | null } = { entry: null };
    const running = (async () => {
      try {
        await this.run(paneId);
      } finally {
        if (
          self.entry !== null &&
          this.state.get(paneId) === self.entry &&
          self.entry.queued === null
        ) {
          this.state.delete(paneId);
        }
      }
    })();
    // SWALLOWED HERE, at the point the promise is created, so a rejection can never be unhandled --
    // not in the window before a follow-up chains onto it, and not when no follow-up ever comes.
    self.entry = { running, settled: running.catch(() => {}), queued: null };
    this.state.set(paneId, self.entry);
    return running;
  }
}

/**
 * How a fetched overlay answer is IDENTIFIED, so an identical re-request costs no network call.
 *
 * WHY THIS IS SOUND, AND WHY IT IS NOT THE CACHE THE CARD FORBIDS. Card t_10c01825 forbids caching a
 * response by (asset, tf) "БЕЗ УЧЁТА ФОРМИРУЮЩЕГОСЯ БАРА" -- t_420df4d4's week-box D1/D2, a level
 * frozen on a live pane. The key here INCLUDES the closed-bar identity, and the reason that is
 * enough was EXECUTED rather than read
 * (`process/reviews/t_10c01825/prove_closed_bar_answers.py`, run against the real BTC cache):
 *
 *     BTC/15m: forming bar moved 0 of 7 payloads
 *     BTC/1D:  forming bar moved 0 of 7 payloads
 *     CONTROL, the same fabricated bar served as CLOSED: moved 6 of 7 on both
 *
 * i.e. a forming bar 20% above the series high and 20% below its low changes NO overlay answer,
 * while the same bar as a closed bar changes almost all of them -- so the control proves the probe
 * was not vacuous. Every overlay payload is a function of the CLOSED bars alone, which is what
 * `feeds.HyperliquidFeed.bars` claims (audit 2026-07-04 HA-5) and what this key relies on.
 *
 * THE THREE THINGS THAT MUST INVALIDATE IT, and how each does:
 *
 *   * A BAR CLOSED -> `lastClosedTime` moves -> different key -> refetched. This is the one the
 *     card's DO-NOT names, and the live loop already forces a refresh on rollover (`barclose.ts`).
 *   * A HISTORY SPLICE -> the pane's window moved left, so `url` carries a different `first_time`
 *     (`main.ts` re-sends it from `pane.windowFirstTime`) AND `firstTime` differs -> refetched.
 *     `first_time` is already in the url, so the field is belt-and-braces for a caller that ever
 *     stops sending it.
 *   * A KNOB, TOGGLE, ASSET, TIMEFRAME OR SOURCE CHANGE -> all of them are in the url.
 *
 * The forming bar itself is deliberately NOT in the key: putting it there is the same as having no
 * reuse at all (it moves every 5 s), and the probe above is why it does not need to be. The live
 * candle still reaches the chart through `/api/live`, untouched by any of this.
 */
export type OverlayAnswerKey = {
  /** The full request url, query string included. */
  url: string;
  /** The pane's first bar time, or `null` before its window is known. */
  firstTime: number | null;
  /** The pane's last CLOSED bar time, or `null` when the pane holds no closed bar. */
  lastClosedTime: number | null;
};

/** The key as one string. Not for display — only equality is ever asked of it. */
export function overlayAnswerKey(k: OverlayAnswerKey): string {
  return `${k.firstTime ?? 'x'}|${k.lastClosedTime ?? 'x'}|${k.url}`;
}

/**
 * The answers one pane already fetched for its CURRENT closed-bar set.
 *
 * BOUNDED BY CONSTRUCTION, not by an eviction policy: the whole map is DROPPED the moment the pane's
 * closed-bar identity changes, so it holds at most the ~7 layers of one (window, last-closed-bar,
 * knobs) state. An LRU with a size limit would be the shape that keeps a stale answer alive.
 */
export class OverlayAnswerStore<T> {
  private readonly byPane = new Map<string, { stamp: string; answers: Map<string, T> }>();

  /** Reads served from an already-fetched answer, per pane. For witnesses and the probe. */
  private readonly reuseCount = new Map<string, number>();

  /**
   * The answer for this key if it was already fetched for this pane's CURRENT bar set, else
   * `undefined`.
   *
   * A CALL WITH A NEW BAR IDENTITY DROPS EVERYTHING THIS PANE HELD, in the same call. That is the
   * anti-staleness mechanism and it is deliberately eager: a bar closing must not leave one layer's
   * old answer reachable while another layer's is refreshed.
   */
  get(paneId: string, k: OverlayAnswerKey): T | undefined {
    const stamp = `${k.firstTime ?? 'x'}|${k.lastClosedTime ?? 'x'}`;
    const held = this.byPane.get(paneId);
    if (held === undefined || held.stamp !== stamp) {
      this.byPane.set(paneId, { stamp, answers: new Map() });
      return undefined;
    }
    const hit = held.answers.get(overlayAnswerKey(k));
    if (hit !== undefined) this.reuseCount.set(paneId, this.reuse(paneId) + 1);
    return hit;
  }

  /** Remember an answer. Only ever called after a REAL fetch, so nothing here is derived. */
  put(paneId: string, k: OverlayAnswerKey, value: T): void {
    const stamp = `${k.firstTime ?? 'x'}|${k.lastClosedTime ?? 'x'}`;
    const held = this.byPane.get(paneId);
    if (held === undefined || held.stamp !== stamp) {
      this.byPane.set(paneId, { stamp, answers: new Map([[overlayAnswerKey(k), value]]) });
      return;
    }
    held.answers.set(overlayAnswerKey(k), value);
  }

  /** Forget everything this pane holds. Called when its series is REPLACED (asset/tf/source). */
  forget(paneId: string): void {
    this.byPane.delete(paneId);
  }

  /** How many reads this pane served without a fetch. */
  reuse(paneId: string): number {
    return this.reuseCount.get(paneId) ?? 0;
  }

  /** How many answers this pane currently holds. */
  size(paneId: string): number {
    return this.byPane.get(paneId)?.answers.size ?? 0;
  }
}
